import { useMemo, useState } from 'react';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { PartnerPaymentNotices } from '../components/PartnerPaymentNotices';
import { SelectField } from '../components/Form';
import { Chip } from '../components/Chips';
import { EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { ConfirmDialog } from '../components/Overlay';
import { useAuth } from '../lib/auth';
import { partnerConnections, partnerOutbound } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDate, formatDateTime, formatMoney, formatRelative } from '../lib/format';
import type { PartnerConnectionDto, PartnerOutboundDeliveryDto, SendableInvoiceDto } from '../lib/types';

type SendableColumn = 'invoice' | 'date' | 'due' | 'total' | 'balance' | 'sent';

function sendableSortValue(i: SendableInvoiceDto, key: SendableColumn): string | number {
  switch (key) {
    case 'invoice':
      return i.docNumber ?? i.qboInvoiceId;
    case 'date':
      return i.txnDate ?? '';
    case 'due':
      return i.dueDate ?? '';
    case 'total':
      return i.totalAmt ?? 0;
    case 'balance':
      return i.balance ?? 0;
    case 'sent':
      return i.sentStatus ?? '';
    default:
      return '';
  }
}

type SentColumn = 'invoice' | 'to' | 'total' | 'status' | 'delivery' | 'sent';

function sentSortValue(d: PartnerOutboundDeliveryDto, key: SentColumn): string | number {
  switch (key) {
    case 'invoice':
      return d.qboDocNumber ?? d.qboInvoiceId;
    case 'to':
      return d.counterpartyCompanyName ?? '';
    case 'total':
      return d.amountIncludingTax ?? 0;
    case 'status':
      return d.status;
    case 'delivery':
      return d.deliveryId ?? '';
    case 'sent':
      return d.createdUtc;
    default:
      return '';
  }
}

const STATUS_OPTIONS = [
  { value: 'Prepared', label: 'Prepared — awaiting iERP AI' },
  { value: 'Accepted', label: 'Accepted — iERP AI is delivering it' },
  { value: 'Failed', label: 'Failed — the call did not complete' },
  { value: 'Refused', label: 'Refused — iERP AI said no' },
];

function statusTone(status: string | null): 'good' | 'warn' | 'bad' | 'neutral' {
  switch (status) {
    case 'Accepted':
      return 'good';
    case 'Failed':
    case 'Refused':
      return 'bad';
    case 'Prepared':
      return 'warn';
    default:
      return 'neutral';
  }
}

/** Only an accepted connection where THIS company is the seller can carry an invoice out. */
function isSellerSide(c: PartnerConnectionDto): boolean {
  return !c.isBuyer && c.status === 'Accepted';
}

function connectionLabel(c: PartnerConnectionDto): string {
  const name = c.counterpartyCompanyName ?? 'Unnamed partner';
  if (!c.boundQboCustomerId) return `${name} — not bound to a QuickBooks customer yet`;
  if (!c.sellerInvoiceSendingEnabled) return `${name} — sending paused`;
  return name;
}

/**
 * SENDING AN INVOICE TO A TRADING PARTNER — one at a time, chosen by a person.
 *
 * There is deliberately no "send everything new" here. An invoice arriving at another company
 * tells them they owe money, and the first version of this path makes a person decide each one;
 * automation can come once the path has been seen to work against real partners, not before.
 *
 * WHAT MAKES A SEND SAFE is enforced by the server and only reflected here: the connection must be
 * accepted and this company must be the SELLER on it, sending must be switched on, the connection
 * must be bound to the QuickBooks customer it invoices, and the invoice must be for THAT customer -
 * whatever id the request carried. This screen shows only seller-side connections, marks the ones
 * that cannot send yet, and says why.
 *
 * SENDING TWICE IS NOT A SECOND INVOICE. Every (connection, invoice) has one row and one stable
 * canonical id; a second press finds the accepted row and sends nothing, and a retry of a failed
 * send presents the same id to iERP AI, whose own idempotency is keyed on it.
 */
export function PartnerOutboundPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const canSend = can('partners.manage');

  const [connectionId, setConnectionId] = useState('');
  const [pendingSend, setPendingSend] = useState<SendableInvoiceDto | null>(null);
  const [busy, setBusy] = useState(false);

  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const connections = useAsync((signal) => partnerConnections.list(signal), []);

  const sellerConnections = useMemo(
    () => (connections.data ?? []).filter(isSellerSide),
    [connections.data],
  );

  const selected = sellerConnections.find((c) => c.connectionId === connectionId) ?? null;
  const selectedCanSend = !!selected && !!selected.boundQboCustomerId && selected.sellerInvoiceSendingEnabled;

  const sendable = useAsync(
    (signal) =>
      connectionId ? partnerOutbound.sendable(connectionId, signal) : Promise.resolve([] as SendableInvoiceDto[]),
    [connectionId],
  );

  const sent = useAsync(
    (signal) =>
      partnerOutbound.list(
        { status: status || null, connectionId: connectionId || null, page, pageSize },
        signal,
      ),
    [status, connectionId, page, pageSize],
  );

  const sendableTable = useSortableTable<SendableColumn>({
    storageKey: 'partnerOutboundSendableColumnWidths',
    columns: {
      invoice: { minWidth: 150 },
      date: { minWidth: 110 },
      due: { minWidth: 110 },
      total: { minWidth: 100 },
      balance: { minWidth: 100 },
      sent: { minWidth: 130 },
    },
  });
  const sentTable = useSortableTable<SentColumn>({
    storageKey: 'partnerOutboundSentColumnWidths',
    columns: {
      invoice: { minWidth: 150 },
      to: { minWidth: 160 },
      total: { minWidth: 100 },
      status: { minWidth: 130 },
      delivery: { minWidth: 160 },
      sent: { minWidth: 130 },
    },
  });

  const send = async (invoice: SendableInvoiceDto) => {
    if (!connectionId) return;
    setBusy(true);
    try {
      const delivery = await partnerOutbound.send(connectionId, invoice.qboInvoiceId);
      if (delivery.status === 'Accepted') {
        toast.success(
          `${delivery.qboDocNumber ?? delivery.qboInvoiceId} accepted by iERP AI for delivery to ${
            delivery.counterpartyCompanyName ?? 'the partner'
          }.`,
        );
      } else {
        toast.success(delivery.statusMessage ?? `${delivery.qboDocNumber ?? delivery.qboInvoiceId}: ${delivery.status}.`);
      }
      await sendable.reload();
      await sent.reload();
    } catch (caught) {
      toast.error(errorMessage(caught));
      // A Failed or Refused row exists now even though the call reported an error - show it.
      await sendable.reload();
      await sent.reload();
    } finally {
      setBusy(false);
      setPendingSend(null);
    }
  };

  const retry = async (id: string) => {
    setBusy(true);
    try {
      const delivery = await partnerOutbound.retry(id);
      toast.success(delivery.statusMessage ?? `${delivery.qboDocNumber ?? delivery.qboInvoiceId}: ${delivery.status}.`);
      await sent.reload();
      await sendable.reload();
    } catch (caught) {
      toast.error(errorMessage(caught));
      await sent.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Send to Partners"
        description="Send one of this company's QuickBooks invoices to a connected customer, one at a time."
      />

      <Panel title="Choose the partner">
        {connections.loading && !connections.data ? (
          <TableSkeleton rows={2} columns={3} />
        ) : connections.error ? (
          <ErrorState message="Connections could not be read." detail={connections.error} onRetry={connections.reload} />
        ) : sellerConnections.length === 0 ? (
          <EmptyState
            title="No connection where this company is the seller"
            description="Sending needs an accepted connection under Trading Partners where the other company is your customer. Bind it to the QuickBooks customer you invoice and switch invoice sending on; it appears here once it is accepted."
          />
        ) : (
          <>
            <Toolbar>
              <SelectField
                label="Customer connection"
                value={connectionId}
                onChange={(value) => {
                  setConnectionId(value);
                  setPage(1);
                }}
                options={sellerConnections.map((c) => ({ value: c.connectionId, label: connectionLabel(c) }))}
                placeholder="Choose a connected customer"
                className="toolbar-grow"
              />
            </Toolbar>

            {selected ? (
              <Toolbar>
                <Chip tone={selected.boundQboCustomerId ? 'good' : 'warn'}>
                  {selected.boundQboCustomerId
                    ? `bound to QuickBooks customer ${selected.boundQboCustomerId}`
                    : 'not bound to a QuickBooks customer'}
                </Chip>
                <Chip tone={selected.sellerInvoiceSendingEnabled ? 'good' : 'warn'}>
                  {selected.sellerInvoiceSendingEnabled ? 'sending on' : 'sending paused'}
                </Chip>
                <Chip tone={selected.buyerInvoiceReceivingEnabled ? 'good' : 'warn'}>
                  {selected.buyerInvoiceReceivingEnabled ? 'they are receiving' : 'they have paused receiving'}
                </Chip>
              </Toolbar>
            ) : null}

            {selected && !selectedCanSend ? (
              <p className="muted">
                {!selected.boundQboCustomerId
                  ? 'Bind this connection to the QuickBooks customer it invoices under Trading Partners before choosing an invoice.'
                  : 'Invoice sending is paused on this connection. Resume it under Trading Partners first.'}
              </p>
            ) : null}
          </>
        )}
      </Panel>

      {selected ? (
        <Panel title={`Invoices for ${selected.counterpartyCompanyName ?? 'this customer'}`}>
          {sendable.loading && !sendable.data ? (
            <TableSkeleton rows={5} columns={6} />
          ) : sendable.error ? (
            <ErrorState message="QuickBooks did not return this customer's invoices." detail={sendable.error} onRetry={sendable.reload} />
          ) : !sendable.data || sendable.data.length === 0 ? (
            <EmptyState
              title="No invoices for this customer"
              description="QuickBooks has no invoices for the bound customer. Raise the invoice in QuickBooks first; this screen sends what the books already hold."
            />
          ) : (
            <TableWrap busy={sendable.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={sendableTable.columnStyle('invoice')} />
                <col style={sendableTable.columnStyle('date')} />
                <col style={sendableTable.columnStyle('due')} />
                <col style={sendableTable.columnStyle('total')} />
                <col style={sendableTable.columnStyle('balance')} />
                <col style={sendableTable.columnStyle('sent')} />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Invoice"
                    sortKey="invoice"
                    currentSortKey={sendableTable.sortKey}
                    sortDir={sendableTable.sortDir}
                    onSort={sendableTable.toggleSort}
                    style={sendableTable.columnStyle('invoice')}
                    onResizeStart={sendableTable.beginResize('invoice')}
                  />
                  <SortableTh
                    label="Date"
                    sortKey="date"
                    currentSortKey={sendableTable.sortKey}
                    sortDir={sendableTable.sortDir}
                    onSort={sendableTable.toggleSort}
                    style={sendableTable.columnStyle('date')}
                    onResizeStart={sendableTable.beginResize('date')}
                  />
                  <SortableTh
                    label="Due"
                    sortKey="due"
                    currentSortKey={sendableTable.sortKey}
                    sortDir={sendableTable.sortDir}
                    onSort={sendableTable.toggleSort}
                    style={sendableTable.columnStyle('due')}
                    onResizeStart={sendableTable.beginResize('due')}
                  />
                  <SortableTh
                    label="Total"
                    sortKey="total"
                    currentSortKey={sendableTable.sortKey}
                    sortDir={sendableTable.sortDir}
                    onSort={sendableTable.toggleSort}
                    className="num"
                    style={sendableTable.columnStyle('total')}
                    onResizeStart={sendableTable.beginResize('total')}
                  />
                  <SortableTh
                    label="Balance"
                    sortKey="balance"
                    currentSortKey={sendableTable.sortKey}
                    sortDir={sendableTable.sortDir}
                    onSort={sendableTable.toggleSort}
                    className="num"
                    style={sendableTable.columnStyle('balance')}
                    onResizeStart={sendableTable.beginResize('balance')}
                  />
                  <SortableTh
                    label="Sent"
                    sortKey="sent"
                    currentSortKey={sendableTable.sortKey}
                    sortDir={sendableTable.sortDir}
                    onSort={sendableTable.toggleSort}
                    style={sendableTable.columnStyle('sent')}
                    onResizeStart={sendableTable.beginResize('sent')}
                  />
                  <th scope="col" className="col-actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sendableTable.sort(sendable.data, sendableSortValue).map((i) => (
                  <tr key={i.qboInvoiceId}>
                    <td>
                      <span className="cell-strong">{i.docNumber ?? i.qboInvoiceId}</span>
                      <span className="cell-sub mono">QuickBooks {i.qboInvoiceId}</span>
                    </td>
                    <td>{formatDate(i.txnDate)}</td>
                    <td>{formatDate(i.dueDate)}</td>
                    <td className="num">{formatMoney(i.totalAmt, i.currencyCode)}</td>
                    <td className="num">{formatMoney(i.balance, i.currencyCode)}</td>
                    <td>
                      {i.sentStatus ? (
                        <>
                          <Chip tone={statusTone(i.sentStatus)}>{i.sentStatus}</Chip>
                          <span className="cell-sub">
                            {i.acceptedUtc ? formatRelative(i.acceptedUtc) : ''}
                          </span>
                        </>
                      ) : (
                        <span className="muted">not sent</span>
                      )}
                    </td>
                    <td className="col-actions">
                      {canSend && selectedCanSend && i.sentStatus !== 'Accepted' ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={busy}
                          onClick={() => setPendingSend(i)}
                        >
                          {i.sentStatus === 'Failed' || i.sentStatus === 'Refused' ? 'Send again' : 'Send'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Panel>
      ) : null}

      <Panel title="Sent invoices">
        <Toolbar>
          <SelectField
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
            placeholder="All statuses"
            className="toolbar-grow"
          />
        </Toolbar>

        {sent.loading && !sent.data ? (
          <TableSkeleton rows={4} columns={6} />
        ) : sent.error ? (
          <ErrorState message="Sent invoices could not be read." detail={sent.error} onRetry={sent.reload} />
        ) : !sent.data || sent.data.items.length === 0 ? (
          <EmptyState
            title={status ? `Nothing with status ${status}` : 'Nothing has been sent yet'}
            description={
              status
                ? 'Clear the filter to see every send.'
                : connectionId
                  ? 'No invoice has been sent through this connection. Choose one above.'
                  : 'An invoice appears here once it has been sent to a partner.'
            }
          />
        ) : (
          <>
            <TableWrap busy={sent.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={sentTable.columnStyle('invoice')} />
                <col style={sentTable.columnStyle('to')} />
                <col style={sentTable.columnStyle('total')} />
                <col style={sentTable.columnStyle('status')} />
                <col style={sentTable.columnStyle('delivery')} />
                <col style={sentTable.columnStyle('sent')} />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Invoice"
                    sortKey="invoice"
                    currentSortKey={sentTable.sortKey}
                    sortDir={sentTable.sortDir}
                    onSort={sentTable.toggleSort}
                    style={sentTable.columnStyle('invoice')}
                    onResizeStart={sentTable.beginResize('invoice')}
                  />
                  <SortableTh
                    label="To"
                    sortKey="to"
                    currentSortKey={sentTable.sortKey}
                    sortDir={sentTable.sortDir}
                    onSort={sentTable.toggleSort}
                    style={sentTable.columnStyle('to')}
                    onResizeStart={sentTable.beginResize('to')}
                  />
                  <SortableTh
                    label="Total"
                    sortKey="total"
                    currentSortKey={sentTable.sortKey}
                    sortDir={sentTable.sortDir}
                    onSort={sentTable.toggleSort}
                    className="num"
                    style={sentTable.columnStyle('total')}
                    onResizeStart={sentTable.beginResize('total')}
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    currentSortKey={sentTable.sortKey}
                    sortDir={sentTable.sortDir}
                    onSort={sentTable.toggleSort}
                    style={sentTable.columnStyle('status')}
                    onResizeStart={sentTable.beginResize('status')}
                  />
                  <SortableTh
                    label="iERP AI delivery"
                    sortKey="delivery"
                    currentSortKey={sentTable.sortKey}
                    sortDir={sentTable.sortDir}
                    onSort={sentTable.toggleSort}
                    style={sentTable.columnStyle('delivery')}
                    onResizeStart={sentTable.beginResize('delivery')}
                  />
                  <SortableTh
                    label="Sent"
                    sortKey="sent"
                    currentSortKey={sentTable.sortKey}
                    sortDir={sentTable.sortDir}
                    onSort={sentTable.toggleSort}
                    style={sentTable.columnStyle('sent')}
                    onResizeStart={sentTable.beginResize('sent')}
                  />
                  <th scope="col" className="col-actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sentTable.sort(sent.data.items, sentSortValue).map((d) => (
                  <tr key={d.id}>
                    <td>
                      <span className="cell-strong">{d.qboDocNumber ?? d.qboInvoiceId}</span>
                      <span className="cell-sub">
                        <span className="mono">QuickBooks {d.qboInvoiceId}</span>
                        {d.pdfFileName ? ` · ${d.pdfFileName}` : ''}
                      </span>
                    </td>
                    <td>{d.counterpartyCompanyName ?? 'A connected partner'}</td>
                    <td className="num">{formatMoney(d.amountIncludingTax, d.currencyCode)}</td>
                    <td>
                      <Chip tone={statusTone(d.status)}>{d.status}</Chip>
                      {d.attemptCount > 1 ? <span className="cell-sub">{d.attemptCount} attempts</span> : null}
                      {d.statusMessage ? <span className="cell-sub">{d.statusMessage}</span> : null}
                    </td>
                    <td>
                      {d.deliveryId ? (
                        <>
                          <span className="mono">{d.deliveryId}</span>
                          <span className="cell-sub">
                            {d.deliveryMethod ?? ''}
                            {d.acceptedUtc ? ` · accepted ${formatDateTime(d.acceptedUtc)}` : ''}
                          </span>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {formatRelative(d.createdUtc)}
                      <span className="cell-sub">{d.createdByEmail ?? ''}</span>
                    </td>
                    <td className="col-actions">
                      {canSend && (d.status === 'Failed' || d.status === 'Refused') ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void retry(d.id)}
                        >
                          Retry
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={sent.data.page}
              pageSize={sent.data.pageSize}
              totalCount={sent.data.totalCount}
              totalPages={sent.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>

      <ConfirmDialog
        open={pendingSend !== null}
        title="Send this invoice?"
        message={
          pendingSend
            ? `${pendingSend.docNumber ?? pendingSend.qboInvoiceId} for ${formatMoney(
                pendingSend.totalAmt,
                pendingSend.currencyCode,
              )} will be handed to iERP AI for delivery to ${
                selected?.counterpartyCompanyName ?? 'the partner'
              }. They will see it as an invoice from this company. This is not undone from here once accepted.`
            : ''
        }
        confirmLabel="Send invoice"
        pending={busy}
        onConfirm={() => {
          if (pendingSend) void send(pendingSend);
        }}
        onCancel={() => setPendingSend(null)}
      />

      {/* The payment leg: what buyers reported paying against the invoices above, and what this
          company reported to sellers about bills it paid. */}
      <PartnerPaymentNotices connectionId={connectionId || null} />
    </>
  );
}
